import { CircularProgress } from "@material-ui/core";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../../_metronic/_partials/controls";
import { getBlogLogsAsync } from "../../redux";
import { tagListAsync } from "../../../Bdc_tag/redux/tagApi";
import { categoryListAsync } from "../../../bdc_Category/redux/categoryApi";
import { authorListAsync } from "../../../Bdc_author/redux/authorManagementApi";
import "./bdcBlogSnapshotView.scss";

const statusLabelMap = {
  draft: "Draft",
  published: "Published",
  pending_review: "Pending Review",
  future: "Future",
  auto_draft: "Auto Draft",
};
const getStatusLabel = (status) => {
  if (status && statusLabelMap[status]) {
    return statusLabelMap[status];
  }
  return status;
};

// Read-only "label above value" field, laid out the same way as the
// Edit Blog page's <div className="form-group"> blocks, just without an
// editable input - this page is a point-in-time viewer, not a form.
// Mirrors blog/pages/blogLog/blogSnapshotView.js.
const Field = ({ label, children, className, style }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <div
      className={`form-control re_inputRouded${className ? ` ${className}` : ""}`}
      style={{ minHeight: 42, ...style }}
    >
      {children ?? <span className="text-muted">-</span>}
    </div>
  </div>
);

const BdcBlogSnapshotView = () => {
  const { blogId, logId, which } = useParams();
  const dispatch = useDispatch();
  // No reducer of its own registered in rootReducer.js - see the note in
  // bdcBlogLog.js. blogLogs comes from the shared state.blog slice.
  const { isLogsLoading, blogLogs } = useSelector(
    (state) => state.blog,
    shallowEqual
  );
  const { tags } = useSelector((state) => state.tag, shallowEqual);
  const { categories } = useSelector((state) => state.category, shallowEqual);
  const { authors } = useSelector((state) => state.author, shallowEqual);

  useEffect(() => {
    if (blogId) {
      dispatch(getBlogLogsAsync(blogId));
    }
    dispatch(tagListAsync());
    dispatch(categoryListAsync());
    dispatch(authorListAsync());
  }, [blogId, dispatch]);

  const log = blogLogs?.find((l) => String(l._id) === String(logId));
  const data = log ? (which === "old" ? log.oldData : log.newData) : null;
  const heading = which === "old" ? "Old Blog" : "New Blog";

  // Resolve raw tag/category/author ids to their display names, using
  // the same option lists (id -> {value, label}) the Edit Blog form
  // already loads - falls back to the raw id if a reference can no
  // longer be resolved (e.g. the tag/category/author was later deleted).
  const resolveNames = (ids, options) => {
    if (!Array.isArray(ids) || !ids.length) return null;
    const byId = {};
    (options || []).forEach((opt) => {
      byId[opt.value] = opt.label;
    });
    return ids.map((id) => byId[id] || id).join(", ");
  };
  const resolveOneName = (id, options) => {
    // bdcblog's `author` field is stored as a single-element array in
    // some documents rather than a scalar id (see bdcblogService.js's
    // create/update) - unwrap defensively so the name still resolves.
    const actualId = Array.isArray(id) ? id[0] : id;
    if (!actualId) return null;
    const match = (options || []).find((opt) => opt.value === actualId);
    return match ? match.label : actualId;
  };

  if (isLogsLoading) {
    return (
      <div style={{ flex: "0 0 auto" }}>
        <div style={{ justifyContent: "center" }} className="text-center">
          <div className="basic-verification-loader text-center">
            <CircularProgress />
          </div>
        </div>
      </div>
    );
  }

  if (!log || !data) {
    return (
      <Card>
        <CardHeader
          title={
            <div className="form-group m-0 d-flex align-items-center">
              <Link className="btn btn-link px-0" to={`/bdc_blog_log/${blogId}`}>
                <SVG
                  className="h-50 align-self-center"
                  src={toAbsoluteUrl(
                    "/media/svg/icons/Navigation/Arrow-left.svg"
                  )}
                />
                Back
              </Link>
              <span className="pl-2">
                Bdc Blog - {heading} Snapshot
                {log?.name ? ` - ${log.name}` : ""}
              </span>
            </div>
          }
        ></CardHeader>
        <CardBody>
          <div className="text-center py-5">
            {!log
              ? "History entry not found."
              : `No ${which === "old" ? "before" : "after"} data recorded for this action.`}
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Row>
      <Col md={8}>
        <Card>
          <CardHeader
            title={
              <div className="form-group m-0 d-flex align-items-center">
                <Link className="btn btn-link px-0" to={`/bdc_blog_log/${blogId}`}>
                  <SVG
                    className="h-50 align-self-center"
                    src={toAbsoluteUrl(
                      "/media/svg/icons/Navigation/Arrow-left.svg"
                    )}
                  />
                  Back
                </Link>
                <span className="pl-2">
                  Bdc Blog - {heading} Snapshot
                  {data?.title ? ` - ${data.title}` : ""}
                </span>
              </div>
            }
          ></CardHeader>
          <CardBody>
            <Row className="justify-content-center">
              <Col lg={12}>
                <Field label="Slug">{data.slug}</Field>
                <Field label="Title">{data.title}</Field>
                <Field
                  label="Description"
                  className="blog-description-preview"
                  style={{
                    height: "auto",
                    maxHeight: 400,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: data.description || "" }}
                  />
                </Field>
                <Field label="Tag">{resolveNames(data.tag, tags)}</Field>
                <Field label="Category">
                  {resolveNames(data.category, categories)}
                </Field>
                <Field label="BLOG: CTA Title">{data.blogCTATitel}</Field>
                <Field label="BLOG: CTA Description">
                  {data.blogCTADescription}
                </Field>
                <Field label="BLOG: CTA Button">{data.blogCTAButton}</Field>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={
              <div className="form-group m-0 d-flex align-items-center">
                <span className="pl-2">FAQ's</span>
              </div>
            }
          ></CardHeader>
          <CardBody>
            <div className="form-group">
              {Array.isArray(data.faqs) && data.faqs.length ? (
                data.faqs.map((faq, index) => (
                  <div key={faq._id || index} className="faq-section">
                    <div className="form-group">
                      <label>Question</label>
                      <div
                        className="form-control"
                        style={{
                          height: "auto",
                          maxHeight: 200,
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {faq.question}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Answer</label>
                      <div
                        className="form-control"
                        style={{
                          height: "auto",
                          maxHeight: 200,
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <span className="text-muted">No FAQs.</span>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>

      <Col md={4}>
        <Card>
          <CardHeader
            title={
              <div className="form-group m-0 d-flex align-items-center">
                <span className="pl-2">Blog Attribute</span>
              </div>
            }
          ></CardHeader>
          <CardBody>
            <div className="col-md-12 text-center mb-4">
              <label className="form-label">Featured Image</label>
              <div
                className="pro_img"
                style={{
                  backgroundImage: `url("${data.image || ""}")`,
                  height: 120,
                  width: "100%",
                  borderRadius: 10,
                  backgroundSize: "contain",
                  border: "1px solid #cccccc",
                }}
              />
            </div>

            <div className="col-md-12 text-center mb-4">
              <label className="form-label">Banner Image</label>
              <div
                className="pro_img"
                style={{
                  backgroundImage: `url("${data.bannerImage || ""}")`,
                  height: 120,
                  width: "100%",
                  borderRadius: 10,
                  backgroundSize: "contain",
                  border: "1px solid #cccccc",
                }}
              />
            </div>

            <Field label="SEO : Meta Title">{data.metaTitle}</Field>
            <Field label="SEO : Meta Description">
              {data.metaDescription}
            </Field>
            <Field label="SEO : Meta Keywords">{data.metaKeywords}</Field>
            <Field label="Author">{resolveOneName(data.author, authors)}</Field>
            <Field label="Created Date">
              {data.createdOn
                ? new Date(data.createdOn).toLocaleString()
                : null}
            </Field>
            <Field label="Updated On">
              {data.updatedOn
                ? new Date(data.updatedOn).toLocaleString()
                : null}
            </Field>
            <Field label="Status">
              <div style={{ textTransform: "capitalize" }}>
                {getStatusLabel(data.status)}
              </div>
            </Field>
            <Field label="Deleted">{data.isDeleted ? "Yes" : "No"}</Field>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default BdcBlogSnapshotView;
